
import { useState, useEffect } from 'react';
import { lazy, Suspense } from "react";
import { usePageTitle } from '@/hooks/usePageTitle';
import { useInbox, INBOX_KEY } from '@/hooks/useInbox';
import { useAuth } from '@/contexts/AuthContext';
import { chatHub } from '@/components/chat/chat-hub';
import { storage } from '@/lib/storage';
import { useQueryClient } from '@tanstack/react-query';
import { ListSkeleton } from '@/components/ui/skeletons';

const ConversationList = lazy(
  () => import('@/components/inbox/conversation-list').then(m => ({ default: m.ConversationList }))
);
const ConversationView = lazy(
  () => import('@/components/inbox/conversation-view').then(m => ({ default: m.ConversationView }))
);

function InboxPage() {
  usePageTitle('Inbox');

  const { data: conversations, isLoading } = useInbox();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { currentOrg, isSuperAdmin } = useAuth();
  const qc = useQueryClient();

  // Request browser notification permission once
  useEffect(() => {
    if (!isSuperAdmin) return;
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      void Notification.requestPermission();
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    if (!isSuperAdmin) return;

    chatHub.configure('', () => storage.getSync('token'));

    // JoinInbox without requiring a client-side orgId — the server resolves the
    // support org from config (Chat:SupportOrgId). Pass currentOrg.id as hint
    // but the server-side fix ensures the support org group is always joined.
    const orgHint = currentOrg?.id;
    const joinInbox = () => void chatHub.joinInbox(orgHint);
    void chatHub.connect().then(joinInbox);
    const retryTimer = setTimeout(() => {
      void chatHub.connect().then(joinInbox);
    }, 3000);

    const offInbox = chatHub.onInboxUpdate((data) => {
      void qc.invalidateQueries({ queryKey: [INBOX_KEY] });

      // Notify when a visitor requests a human agent (tab may be in background)
      if (data.status === 'HumanRequested' && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        new Notification('Support request', {
          body: 'A visitor is asking for a human agent.',
          icon: '/icons/icon-192.png',
          tag: `inbox-${data.id}`,
        });
      }
    });

    return () => { clearTimeout(retryTimer); offInbox(); chatHub.leaveInbox(); };
  }, [currentOrg, isSuperAdmin, qc]);

  if (!isSuperAdmin) {
    return (
      <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
        Access denied.
      </div>
    );
  }

  return (
    <div className="flex flex-1 min-h-0 border rounded-lg overflow-hidden">
      {/* Conversation list — hidden on mobile when a conversation is open */}
      <div className={`${selectedId ? 'hidden md:flex' : 'flex'} w-full md:w-72 shrink-0 flex-col md:border-r overflow-hidden`}>
        <div className="px-4 py-3 border-b shrink-0">
          <h2 className="text-sm font-semibold">Support Inbox</h2>
          {conversations && (
            <span className="mt-1 inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
              {conversations.filter(c => c.status !== 'Resolved' && c.status !== 'Closed').length} open
            </span>
          )}
        </div>
        <div className="flex-1 overflow-y-auto min-h-0 overscroll-contain">
          {isLoading ? (
            <div className="p-3">
              <ListSkeleton rows={3} />
            </div>
          ) : (
            <ConversationList
              conversations={conversations ?? []}
              selectedId={selectedId}
              onSelect={setSelectedId}
            />
          )}
        </div>
      </div>

      {/* Conversation view — hidden on mobile when nothing is selected */}
      <div className={`${selectedId ? 'flex' : 'hidden md:flex'} flex-1 flex-col min-w-0 min-h-0`}>
        {selectedId ? (
          <ConversationView convId={selectedId} onBack={() => setSelectedId(null)} />
        ) : (
          <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
            Select a conversation
          </div>
        )}
      </div>
    </div>
  );
}

import { createFileRoute } from '@tanstack/react-router';
export const Route = createFileRoute('/dashboard/inbox')({ component: InboxPage });
